import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  resetUserChanges,
  updateAddressField,
  updateUser,
} from "@/redux/feature/user/userSlice";
import { useUpdateCreatorMutation } from "@/redux/feature/user/userApi";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import { CustomButton } from "@/components/ui/Buttons";
import { FileDetails, TUser } from "@/types/types";
import { useEffect, useState } from "react";

import FileUploadProfileDesign from "@/components/ui/FileUploadProfileDesign";

import {
  AGE_GROUP_OPTIONS,
  BODY_TYPE_OPTIONS,
  GENDER_OPTIONS,
  SKIN_TONE_OPTIONS,
} from "@/constant/applicationFormConstant";
import {
  INDIAN_STATES,
  MAJOR_CITIES,
  MAJOR_CITIES_BY_COUNTRY,
  MAJOR_CITIES_BY_STATE,
  STATES_BY_COUNTRY,
} from "@/constant";
import AuthOptionGroup from "@/pages/authenticationV2/signUp/components/AuthOptionGroup";
import AuthDropdown, {
  AuthDropdownOption,
} from "@/pages/authenticationV2/signUp/components/AuthDropdown";
import AuthInput from "@/pages/authenticationV2/signUp/components/AuthInput";
import AuthPhoneInput from "@/pages/authenticationV2/signUp/components/AuthPhoneInput";

interface Props {
  data: TUser;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditPersonalDetailsModal = ({ data, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const [updateCreator] = useUpdateCreatorMutation();

  const [errors, setErrors] = useState<Record<string, string>>({});

  // -------------------- Selected Dropdown Options --------------------
  const selectedAge =
    AGE_GROUP_OPTIONS.find((o) => o.key === user?.ageGroup) ?? null;

  const selectedGender =
    GENDER_OPTIONS.find((o) => o.key === user?.gender) ?? null;

  const selectedBodyType =
    BODY_TYPE_OPTIONS.find((o) => o.key === user?.bodyType) ?? null;

  const selectedSkinTone =
    SKIN_TONE_OPTIONS.find((o) => o.key === user?.skinTone) ?? null;

  // -------------------- Location State --------------------
  const countryValue = user?.address?.country ?? "";
  const stateValue = user?.address?.state ?? "";
  const cityValue = user?.address?.city ?? "";

  const [stateOptions, setStateOptions] = useState<AuthDropdownOption[]>([]);
  const [cityOptions, setCityOptions] = useState<AuthDropdownOption[]>([]);

  const matchByKeyOrLabel = (o: AuthDropdownOption, stored: string) =>
    String(o.key) === String(stored) || String(o.label) === String(stored);

  const selectedState =
    stateOptions.find((o) => matchByKeyOrLabel(o, String(stateValue))) ?? null;

  const selectedCity =
    cityOptions.find((o) => matchByKeyOrLabel(o, String(cityValue))) ?? null;

  // Recompute state options when country changes
  useEffect(() => {
    const states =
      STATES_BY_COUNTRY[countryValue] ||
      (countryValue === "India" ? INDIAN_STATES : []);
    setStateOptions(
      (states || []).map((s) => ({ key: s.title, label: s.title })),
    );
  }, [countryValue]);

  // Recompute city options when country or state changes
  useEffect(() => {
    const byState = MAJOR_CITIES_BY_STATE?.[countryValue]?.[stateValue];
    if (byState?.length) {
      setCityOptions(byState.map((c) => ({ key: c.title, label: c.title })));
      return;
    }
    const byCountry = MAJOR_CITIES_BY_COUNTRY?.[countryValue];
    if (byCountry?.length) {
      setCityOptions(byCountry.map((c) => ({ key: c.title, label: c.title })));
      return;
    }
    setCityOptions(
      (MAJOR_CITIES || []).map((c) => ({ key: c.title, label: c.title })),
    );
  }, [countryValue, stateValue]);

  // -------------------- Location Handlers --------------------
  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleStateChange = (opt: AuthDropdownOption) => {
    dispatch(updateAddressField({ field: "state", value: String(opt.label) }));
    dispatch(updateAddressField({ field: "city", value: "" }));
    clearFieldError("state");
    clearFieldError("city");
  };

  const handleCityChange = (opt: AuthDropdownOption) => {
    dispatch(updateAddressField({ field: "city", value: String(opt.label) }));
    clearFieldError("city");
  };

  // -------------------- Update Handler --------------------
  const handlerUpdateCreator = async () => {
    if (!data?.creator_id) {
      toast.error("User information is missing");
      return;
    }

    const age = Number(user?.age);
    if (!Number.isInteger(age) || age < 1 || age > 100) {
      setErrors((prev) => ({
        ...prev,
        age: "Enter a valid age between 1 and 100",
      }));
      return;
    }

    const payload = {
      id: data.user_id,
      body: {
        fullName: user.fullName,
        gender: user.gender,
        age,
        ageGroup: user.ageGroup,
        phoneNumber: user.phoneNumber || "",
        bio: user.bio || "",
        address: {
          // country: user.address?.country || "",
          city: user.address?.city || "",
          state: user.address?.state || "",
          zipCode: user.address?.zipCode || "",
          mapLink: user.address?.mapLink || "",
        },
        skinTone: user.skinTone || "",
        ...(user.bodyType ? { bodyType: user.bodyType } : {}),
        profilePhoto: user.profilePhoto || null,
      },
    };

    await handleApiRequest({
      payload,
      requestFunction: updateCreator,
      onSuccess: () => {
        toast.success("Personal details updated successfully!");
        onClose(false);
      },
      onError: () => {
        toast.error("Failed to update personal details.");
      },
    });
  };

  const handlerCancel = () => {
    dispatch(resetUserChanges(data));
    onClose(false);
  };

  // -------------------- UI --------------------
  return (
    <div className="bg-white rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center p-6 border-b">
        <h3 className="text-lg font-semibold text-[#101828]">
          Edit Personal Details
        </h3>
        <button onClick={handlerCancel}>
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Body */}
      <div className="p-6 space-y-6">
        {/* Profile Photo */}
        <FileUploadProfileDesign
          useExternalState
          externalFiles={
            user?.profilePhoto ? [user.profilePhoto as any as FileDetails] : []
          }
          setExternalFiles={(next) => {
            const nextArr = typeof next === "function" ? next([]) : next;
            dispatch(
              updateUser({
                field: "profilePhoto",
                value: nextArr[0] ?? null,
              }),
            );
          }}
        />

        {/* Full Name */}
        <AuthInput
          label="Full Name"
          name="fullName"
          value={user?.fullName}
          placeholder="Enter your full name"
          onChange={(e) =>
            dispatch(updateUser({ field: "fullName", value: e.target.value }))
          }
        />

        {/* Phone */}
        <div id="phoneNumber">
          <AuthPhoneInput
            name="phone"
            label="Phone number"
            value={user?.phoneNumber}
            onChange={(value) =>
              dispatch(updateUser({ field: "phoneNumber", value }))
            }
          />
        </div>

        {/* Age + Gender */}
        <div className="flex gap-6">
          <div id="age" className="w-full">
            <AuthInput
              label="Age"
              name="age"
              type="number"
              min={1}
              max={100}
              step={1}
              placeholder="Enter your age"
              value={user?.age ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (
                  value === "" ||
                  (/^\d+$/.test(value) && Number(value) <= 100)
                ) {
                  dispatch(
                    updateUser({
                      field: "age",
                      value: value === "" ? undefined : Number(value),
                    }),
                  );
                  clearFieldError("age");
                }
              }}
              error={errors.age}
            />
          </div>

          <div className="w-full">
            <AuthDropdown
              label="Gender"
              options={GENDER_OPTIONS}
              value={selectedGender}
              onChange={(opt) =>
                dispatch(
                  updateUser({ field: "gender", value: String(opt.key) }),
                )
              }
            />
          </div>
        </div>

        {/* Country - Read Only */}
        <div id="country">
          <label className="block text-sm font-medium text-[#344054] mb-1.5">
            Country
          </label>
          <div className="w-full px-3.5 py-2.5 rounded-lg border border-[#D0D5DD] bg-[#F9FAFB] text-[#344054] text-sm cursor-not-allowed">
            {countryValue || "—"}
          </div>
        </div>

        {/* State + City */}
        <div className="flex items-start flex-col md:flex-row gap-5 lg:gap-x-[26px]">
          <div className="w-full" id="state">
            <AuthDropdown
              label="State"
              options={stateOptions}
              value={selectedState}
              onChange={handleStateChange}
              onOptionsChange={setStateOptions}
              error={errors.state}
              others
            />
          </div>

          <div className="w-full" id="city">
            <AuthDropdown
              label="City"
              options={cityOptions}
              value={selectedCity}
              onChange={handleCityChange}
              onOptionsChange={setCityOptions}
              error={errors.city}
              others
            />
          </div>
        </div>

        {/* Zip Code */}
        <AuthInput
          label="Zip Code"
          name="zipCode"
          value={user?.address?.zipCode}
          placeholder="Enter your zip / postal code"
          autoComplete="postal-code"
          onChange={(e) =>
            dispatch(
              updateAddressField({ field: "zipCode", value: e.target.value }),
            )
          }
        />

        {/* Map Link */}
        <AuthInput
          label="Google Map Link (Optional)"
          name="mapLink"
          placeholder="Paste Google map link"
          value={user?.address?.mapLink ?? ""}
          onChange={(e) =>
            dispatch(
              updateAddressField({ field: "mapLink", value: e.target.value }),
            )
          }
        />

        {/* Body Type */}
        <AuthOptionGroup
          label="Body Type"
          options={BODY_TYPE_OPTIONS}
          value={selectedBodyType}
          onChange={(opt) =>
            dispatch(updateUser({ field: "bodyType", value: String(opt.key) }))
          }
        />

        {/* Skin Tone */}
        <AuthOptionGroup
          label="Skin Tone"
          options={SKIN_TONE_OPTIONS}
          value={selectedSkinTone}
          onChange={(opt) =>
            dispatch(updateUser({ field: "skinTone", value: String(opt.key) }))
          }
        />

        {/* Bio */}
        <AuthInput
          label="Bio"
          name="bio"
          value={user?.bio}
          placeholder="Write something about yourself..."
          onChange={(e) =>
            dispatch(updateUser({ field: "bio", value: e.target.value }))
          }
        />
      </div>

      {/* Footer */}
      <div className="flex gap-4 p-6 border-t">
        <CustomButton
          variant="outline"
          className="w-full"
          onClick={handlerCancel}
        >
          Cancel
        </CustomButton>

        <CustomButton
          className="w-full bg-[#FF6600] text-white"
          onClick={handlerUpdateCreator}
        >
          Save Changes
        </CustomButton>
      </div>
    </div>
  );
};

export default EditPersonalDetailsModal;
